import React from "react";
import { motion } from "framer-motion";
import { Shield, FileText, ShoppingCart, ChevronDown } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

const interFont = {
  fontFamily: '"Inter", ui-sans-serif, system-ui, sans-serif',
} as const;

const ROLE_KEYS = ["admin", "manager", "operator"] as const;

const ROLE_ICONS = {
  admin: Shield,
  manager: FileText,
  operator: ShoppingCart,
} as const;

const ROLE_STYLES = {
  admin: {
    iconBg: "bg-violet-100",
    iconColor: "text-violet-600",
  },
  manager: {
    iconBg: "bg-[#FFE1EC]",
    iconColor: "text-meno-pink",
  },
  operator: {
    iconBg: "bg-emerald-100",
    iconColor: "text-emerald-600",
  },
} as const;

const MEMBER_KEYS = ["owner", "manager", "operator"] as const;

const MEMBER_STYLES = {
  owner: {
    avatarBg: "bg-meno-pink",
    avatarText: "text-white",
    roleColor: "text-violet-600",
  },
  manager: {
    avatarBg: "bg-violet-100",
    avatarText: "text-violet-700",
    roleColor: "text-meno-pink",
  },
  operator: {
    avatarBg: "bg-emerald-100",
    avatarText: "text-emerald-700",
    roleColor: "text-emerald-600",
  },
} as const;

function TeamMembersCard() {
  const { t } = useLanguage();

  return (
    <div
      className="w-full max-w-[520px] rounded-2xl border border-gray-100 bg-white p-6 shadow-xl shadow-gray-900/8 sm:p-8"
      style={interFont}
      role="img"
      aria-label={t("teamRoles.cardAria")}
    >
      <div
        className={`mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between`}
      >
        <div className="text-start">
          <h3 style={{ lineHeight: 0 }}>
            <span className="text-xl font-bold text-gray-900">
              {t("teamRoles.cardTitle")}
            </span>
          </h3>
          <p style={{ lineHeight: 0 }}>
            <span className="text-sm text-gray-400 mt-1">
              {t("teamRoles.cardWorkspace")}
            </span>
          </p>
        </div>
        <button
          type="button"
          tabIndex={-1}
          className="shrink-0 rounded-xl bg-gray-900 px-4 py-2.5 text-sm font-semibold text-white cursor-default w-fit"
        >
          {t("teamRoles.inviteButton")}
        </button>
      </div>

      <div
        className={`mb-3 hidden grid-cols-[1fr_auto] gap-4 px-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400 sm:grid`}
      >
        <span>{t("teamRoles.colUser")}</span>
        <span>{t("teamRoles.colRole")}</span>
      </div>

      <div className="space-y-1">
        {MEMBER_KEYS.map((key) => {
          const style = MEMBER_STYLES[key];
          return (
            <div
              key={key}
              className={`flex items-center justify-between gap-4 rounded-xl px-1 py-3.5`}
            >
              <div
                className={`flex min-w-0 items-center gap-3`}
              >
                <div
                  className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-bold ${style.avatarBg} ${style.avatarText}`}
                >
                  {t(`teamRoles.members.${key}.initials`)}
                </div>
                <div
                  className={`min-w-0 text-start`}
                >
                  <p style={{ lineHeight: 0 }}>
                    <span className="text-sm font-semibold text-gray-900">
                      {t(`teamRoles.members.${key}.name`)}
                    </span>
                  </p>
                  <p style={{ lineHeight: 0 }}>
                    <span className="text-xs text-gray-400">
                      {t(`teamRoles.members.${key}.handle`)}
                    </span>
                  </p>
                </div>
              </div>
              <span
                className={`shrink-0 text-sm font-semibold ${style.roleColor}`}
              >
                {t(`teamRoles.members.${key}.role`)}
              </span>
            </div>
          );
        })}
      </div>

      <div className="mt-5 rounded-xl border border-dashed border-gray-200 bg-gray-50/60 p-4">
        <div
          className={`flex flex-col gap-3 sm:flex-row sm:items-center`}
        >
          <span className="flex-1 text-sm text-gray-400">
            {t("teamRoles.emailPlaceholder")}
          </span>
          <button
            type="button"
            tabIndex={-1}
            className={`inline-flex shrink-0 items-center gap-1.5 rounded-xl w-fit bg-gray-900 px-3 py-2 text-sm font-semibold text-white cursor-default`}
          >
            {t("teamRoles.roles.admin.title")}
            <ChevronDown className="h-4 w-4 opacity-80" aria-hidden="true" />
          </button>
        </div>
      </div>
    </div>
  );
}

export default function MenoTeamRoles() {
  const { t } = useLanguage();
  const textAlign = "text-start";

  const itemReveal = {
    hidden: { opacity: 0, y: 24 },
    show: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.55, ease: "easeOut" },
    },
  };

  return (
    <section className="bg-white py-16 lg:py-24 safari-paint-contain">
      <motion.div
        className="custom-container"
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.15 }}
        variants={{
          hidden: { opacity: 0, y: 40 },
          show: {
            opacity: 1,
            y: 0,
            transition: { duration: 0.6, staggerChildren: 0.1 },
          },
        }}
      >
        <div
          className={`flex flex-col items-center gap-12 lg:flex-row lg:items-center lg:gap-16 xl:gap-24`}
        >
          <motion.div className={`flex-1 ${textAlign}`} variants={itemReveal}>
            <span
              className={`inline-flex items-center bg-[#FFE1EC] text-meno-pink text-xs font-bold tracking-wide px-4 py-1.5 rounded-full mb-5`}
            >
              {t("teamRoles.badge")}
            </span>

            <h2 style={{ lineHeight: 0 }}>
              <span
                className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-gray-900 leading-tight"
                style={interFont}
              >
                {t("teamRoles.title1")}{" "}
                <span className="text-meno-pink">
                  {t("teamRoles.titleAccent")}
                </span>
              </span>
            </h2>

            <p style={{ lineHeight: 0 }}>
              <span
                className="text-lg leading-relaxed text-gray-600 mt-4 max-w-xl block"
                style={interFont}
              >
                {t("teamRoles.desc")}
              </span>
            </p>

            <div className="mt-10 space-y-8">
              {ROLE_KEYS.map((key) => {
                const Icon = ROLE_ICONS[key];
                const style = ROLE_STYLES[key];
                return (
                  <div key={key} className={`flex gap-4`}>
                    <div
                      className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${style.iconBg}`}
                    >
                      <Icon
                        className={`h-5 w-5 ${style.iconColor}`}
                        strokeWidth={2}
                        aria-hidden="true"
                      />
                    </div>
                    <div className="min-w-0">
                      <h3 style={{ lineHeight: 0 }}>
                        <span
                          className="text-base font-bold text-gray-900"
                          style={interFont}
                        >
                          {t(`teamRoles.roles.${key}.title`)}
                        </span>
                      </h3>
                      <p style={{ lineHeight: 0 }}>
                        <span
                          className="text-sm leading-relaxed text-gray-500 mt-1 block"
                          style={interFont}
                        >
                          {t(`teamRoles.roles.${key}.desc`)}
                        </span>
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </motion.div>

          <motion.div
            className="w-full shrink-0 lg:w-[46%] xl:w-[42%] flex justify-center"
            variants={itemReveal}
          >
            <TeamMembersCard />
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
}
